import React, { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { document.title = "Contact — PodiumOne"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", { name, email, message });
      setSent(true);
      toast.success("Message sent — we'll be in touch");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-28 pb-16">
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Contact</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 mb-4">Get in touch</h1>
        <p className="text-white/60 text-lg mb-6">Questions, issues, or Brand Lock help? Send us a message and we'll get back to you.</p>
        <a href="mailto:admin@podiumone.to" data-testid="support-email" className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-sm mb-8 hover:border-white/25 transition-colors">
          <Mail className="w-4 h-4 text-emerald-400" /> admin@podiumone.to
        </a>

        {sent ? (
          <div className="glass-strong rounded-2xl p-8 text-center" data-testid="contact-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display font-bold text-2xl mb-2">Message received</h2>
            <p className="text-white/60">Thanks, {name || "there"} — our team will reply to {email} shortly.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="glass-strong rounded-2xl p-6 space-y-4" data-testid="contact-form">
            <div>
              <Label className="text-white/70 text-sm">Name</Label>
              <Input data-testid="contact-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Email</Label>
              <Input data-testid="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-white/5 border-white/15" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Message</Label>
              <textarea data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} required rows={5} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/15 p-3 text-sm outline-none focus:ring-2 focus:ring-blue-500/50 resize-none" />
            </div>
            <Button data-testid="contact-submit" type="submit" disabled={sending} className="w-full rounded-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 font-semibold active:scale-95 transition-all">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send message</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
